import clsx from "clsx";
import OverflowTextField from "designSystem/components/textfield/OverflowTextField";
import { Box, Button, ConfirmDialog, FlexRow, FormControlLabel, Radio, RadioGroup, Text } from "designSystem/index";
import { action } from "mobx";
import { observer } from "mobx-react-lite";
import React, { Fragment, FunctionComponent, SyntheticEvent, useState } from "react";
import { CELL_TYPE, COLUMN_ATTRIBUTE_ID, COLUMN_ATTRIBUTE_NAME, COLUMN_CUSTOM, NamesStoreItem } from "./CustomViewNamesStore";
import css from "./NamesTab.module.css";

const HEADER_RES = ["CustomViewAttributeId", "CustomViewAttribute", "CustomViewCustom"];

interface HeaderProps {
	id: number;
	labelRes: string;
	onApplyAllClick?:(id: number)=>void;
}

/** render header cell of the table */
const Header: FunctionComponent<HeaderProps> = (props) => {
	const { id, labelRes, onApplyAllClick } = props;
	const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
	return (
		<Box
			className={clsx(css.gridCell, css.header)}
			key={id}
		>
			<Text res={labelRes} />
			{(id < COLUMN_CUSTOM) && (
				<Fragment>
					<Button
						className={css.btnApplyAll}
						design="link"
						onClick={() => setConfirmDialogOpen(true)}
						res="CustomViewApplyToAll"
					/>
					<ConfirmDialog
						category="error"
						confirmRes="Confirm"
						messageRes="CustomViewApplyToAllConfirmText"
						onClose={() => setConfirmDialogOpen(false)}
						onConfirm={() => onApplyAllClick(id)}
						open={confirmDialogOpen}
						titleRes="CustomViewApplyToAllConfirmTitle"
					/>
				</Fragment>
			)}
		</Box>
	);
};

interface CellProps {
	id: number;
	label: string;
	bEdit?: boolean;
	onValueChange?:(event)=>void;
}

/** render data cell of the table */
const Cell: FunctionComponent<CellProps> = observer((props: CellProps) => {
	const {
		id,
		label,
		bEdit,
		onValueChange,
	} = props;

	return (
		<Fragment>
			{bEdit && (
				<FormControlLabel
					className={clsx(css.gridCell, css.cell)}
					control={(
						<Fragment>
							<Radio value={id} />
							<OverflowTextField
								className={css.textField}
								fullWidth
								inputProps={{
									value: label,
								}}
								onChange={onValueChange}
							/>
						</Fragment>
					)}
					labelPlacement="end" 
				/>
			)}
			{!bEdit && (id < COLUMN_CUSTOM) && (
				<FormControlLabel
					className={clsx(css.gridCell, css.cell)}
					control={
						<Radio />
					}
					label={label}
					labelPlacement="end"
					value={id}
					wrap={false}
				/>
			)}
			{!bEdit && (id == COLUMN_CUSTOM) && (
				<FormControlLabel
					className={clsx(css.gridCell, css.cell, css.cellCustom)}
					control={
						<Radio />
					}
					labelPlacement="end"
					res="CustomViewCustomName"
					value={id}
					wrap={false} 
				/>
			)}
		</Fragment>
	);
});

interface NamesRowProps {
	id: number;
	item: NamesStoreItem;
	onApplyAllClick: (column: number)=>void;
}

/** render one row of the table */
const NamesRow: FunctionComponent<NamesRowProps> = observer((props: NamesRowProps) => {
	const { id, item, onApplyAllClick  } = props;
	const { key, name, value } = item;
	const bHeader = id < 0;

	const onUpdateOption = action((ev: SyntheticEvent, column: string): void => {
		item.setValue(~~column);
		item.name = item[CELL_TYPE[column]];
	});

	const onEditorChange = action((ev): void=>{
		item.name =  ev.target.value;
	});

	return (
		<Fragment>
			{bHeader && (
				<FlexRow>
					<Header
						id={COLUMN_ATTRIBUTE_ID}
						labelRes={HEADER_RES[COLUMN_ATTRIBUTE_ID]}
						onApplyAllClick={onApplyAllClick}
					/>
					<Header
						id={COLUMN_ATTRIBUTE_NAME}
						labelRes={HEADER_RES[COLUMN_ATTRIBUTE_NAME]}
						onApplyAllClick={onApplyAllClick}
					/>
					<Header id={COLUMN_CUSTOM} labelRes={HEADER_RES[COLUMN_CUSTOM]} />
				</FlexRow>
			)}
			{!bHeader && (
				<RadioGroup
					className={css.radioGroup}
					data-csi-automation="RadioGroup"
					defaultValue={id}
					id={item.name}
					name={key}
					onChange={onUpdateOption}
					row
					value={value}
				>
					<Cell id={COLUMN_ATTRIBUTE_ID} label={item.attributeId} />
					<Cell id={COLUMN_ATTRIBUTE_NAME} label={item.attributeName} />
					<Cell
						bEdit = {value == COLUMN_CUSTOM}
						id={COLUMN_CUSTOM}
						label={item.name}
						onValueChange={onEditorChange}
					/>
				</RadioGroup>
			)}
		</Fragment>
	);
});

export default NamesRow;